import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ipoService } from '../../api/ipoService';
import { mapIpoFaqs } from '../../utils/ipoFaqs';

/**
 * Async thunk to fetch IPOs from backend
 * GET /api/core/ipos?page=1&page_size=50
 */
export const fetchFeaturedIpos = createAsyncThunk(
  'ipo/fetchFeaturedIpos',
  async (params = { page: 1, page_size: 10 }, { rejectWithValue }) => {
    try {
      const data = await ipoService.getIpos(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch IPOs from server');
    }
  }
);

/**
 * Async thunk to fetch live market IPOs (Current and Upcoming)
 * GET /api/market/ipos/live?force=false
 */
export const fetchLiveIpos = createAsyncThunk(
  'ipo/fetchLiveIpos',
  async (force = false, { rejectWithValue }) => {
    try {
      const data = await ipoService.getLiveIpos(force);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch live IPOs');
    }
  }
);

/**
 * Async thunk to fetch single IPO details by ID
 * GET /api/core/ipos/{ipo_id}
 */
export const fetchIpoDetails = createAsyncThunk(
  'ipo/fetchIpoDetails',
  async (id, { rejectWithValue }) => {
    try {
      const data = await ipoService.getIpoById(id);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch IPO details');
    }
  }
);

/**
 * Async thunk to fetch the FAQs for one IPO
 * GET /api/core/ipos/{ipo_id}/faqs
 */
export const fetchIpoFaqs = createAsyncThunk(
  'ipo/fetchIpoFaqs',
  async (id, { rejectWithValue }) => {
    try {
      const data = await ipoService.getIpoFaqs(id);
      if (data?.success === false) {
        return rejectWithValue(data.error || 'Failed to fetch IPO FAQs');
      }
      return mapIpoFaqs(data);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch IPO FAQs');
    }
  }
);

const initialState = {
  searchQuery: '',
  activeCategory: 'all',
  recentSearches: [],

  // API State for Featured Issues List (Legacy core)
  featuredIpos: [],
  featuredIposLoading: false,
  featuredIposError: null,
  featuredIposTotal: 0,
  featuredIposPage: 1,
  featuredIposPageSize: 10,

  // API State for DRHP Filings Directory (paginated table)

  // API State for Live Market IPOs (Current & Upcoming)
  liveIposCurrent: [],
  liveIposUpcoming: [],
  liveIposLoading: false,
  liveIposError: null,
  liveIposRefreshedAt: null,

  // API State for Single IPO Details Page
  currentIpo: null,
  currentIpoLoading: false,
  currentIpoError: null,

  // FAQs for the IPO detail page ([{ id, question, answer }])
  currentIpoFaqs: [],
  currentIpoFaqsLoading: false,
  currentIpoFaqsError: null,
  currentIpoFaqsRequestId: null,
};

export const ipoSlice = createSlice({
  name: 'ipo',
  initialState,
  reducers: {
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    clearSearchQuery: (state) => {
      state.searchQuery = '';
    },
    setActiveCategory: (state, action) => {
      state.activeCategory = action.payload;
    },
    addRecentSearch: (state, action) => {
      if (action.payload && !state.recentSearches.includes(action.payload)) {
        state.recentSearches.unshift(action.payload);
        if (state.recentSearches.length > 5) {
          state.recentSearches.pop();
        }
      }
    },
    clearCurrentIpo: (state) => {
      state.currentIpo = null;
      state.currentIpoError = null;
      state.currentIpoFaqs = [];
      state.currentIpoFaqsLoading = false;
      state.currentIpoFaqsError = null;
      state.currentIpoFaqsRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchFeaturedIpos
      .addCase(fetchFeaturedIpos.pending, (state) => {
        state.featuredIposLoading = true;
        state.featuredIposError = null;
      })
      .addCase(fetchFeaturedIpos.fulfilled, (state, action) => {
        state.featuredIposLoading = false;
        const payload = action.payload;
        if (payload && Array.isArray(payload.data)) {
          state.featuredIpos = payload.data;
          state.featuredIposTotal = payload.total || payload.data.length;
          state.featuredIposPage = payload.page || 1;
          state.featuredIposPageSize = payload.page_size || 50;
        } else if (Array.isArray(payload)) {
          state.featuredIpos = payload;
          state.featuredIposTotal = payload.length;
          state.featuredIposPage = 1;
          state.featuredIposPageSize = 50;
        } else {
          state.featuredIpos = [];
        }
      })
      .addCase(fetchFeaturedIpos.rejected, (state, action) => {
        state.featuredIposLoading = false;
        state.featuredIposError = action.payload || 'Failed to load IPOs';
      })

      // fetchLiveIpos
      .addCase(fetchLiveIpos.pending, (state) => {
        state.liveIposLoading = true;
        state.liveIposError = null;
      })
      .addCase(fetchLiveIpos.fulfilled, (state, action) => {
        state.liveIposLoading = false;
        const payload = action.payload;
        if (payload?.data) {
          state.liveIposCurrent = Array.isArray(payload.data.current)
            ? payload.data.current
            : [];
          state.liveIposUpcoming = Array.isArray(payload.data.upcoming)
            ? payload.data.upcoming
            : [];
          state.liveIposRefreshedAt = payload.data.refreshed_at || null;
        } else {
          state.liveIposCurrent = [];
          state.liveIposUpcoming = [];
        }
      })
      .addCase(fetchLiveIpos.rejected, (state, action) => {
        state.liveIposLoading = false;
        state.liveIposError = action.payload || 'Failed to load live IPOs';
      })

      // fetchIpoDetails
      .addCase(fetchIpoDetails.pending, (state) => {
        state.currentIpoLoading = true;
        state.currentIpoError = null;
      })
      .addCase(fetchIpoDetails.fulfilled, (state, action) => {
        state.currentIpoLoading = false;
        state.currentIpo = action.payload?.data || action.payload || null;
      })
      .addCase(fetchIpoDetails.rejected, (state, action) => {
        state.currentIpoLoading = false;
        state.currentIpoError = action.payload || 'Failed to load IPO details';
        state.currentIpo = null;
      })

      // fetchIpoFaqs — only the latest request may update the list, so FAQs
      // from a previously viewed IPO can't land on the current page
      .addCase(fetchIpoFaqs.pending, (state, action) => {
        state.currentIpoFaqsLoading = true;
        state.currentIpoFaqsError = null;
        state.currentIpoFaqsRequestId = action.meta.requestId;
      })
      .addCase(fetchIpoFaqs.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentIpoFaqsRequestId) return;
        state.currentIpoFaqsLoading = false;
        state.currentIpoFaqs = action.payload;
      })
      .addCase(fetchIpoFaqs.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentIpoFaqsRequestId) return;
        state.currentIpoFaqsLoading = false;
        state.currentIpoFaqsError = action.payload || 'Failed to load IPO FAQs';
        state.currentIpoFaqs = [];
      });
  },
});

export const {
  setSearchQuery,
  clearSearchQuery,
  setActiveCategory,
  addRecentSearch,
  clearCurrentIpo,
} = ipoSlice.actions;

export default ipoSlice.reducer;
